import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  listAdminServicesApi,
  getAdminServiceApi,
  answerAdminServiceApi,
} from "api/admin";
import {
  C,
  Panel,
  Row,
  SkeletonRows,
  Empty,
  ServiceStatusChip,
  InfoRow,
  DetailFrame,
  SearchBar,
  FilterPills,
  Pagination,
  formatDate,
  relativeTime,
  primaryBtn,
  CsvExportButton,
} from "./admin_ui";

export function AdminServicesList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const search = params.get("q") || "";
  const page = parseInt(params.get("page") || "1", 10) || 1;

  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => setSearchInput(search), [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const r = await listAdminServicesApi({
        status: status || undefined,
        search: search || undefined,
        page,
        limit: 20,
      });
      if (cancelled) return;
      if (r?.success) setData({ items: r.items || [], total: r.total || 0 });
      else setError(r?.error || "조회 실패");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [status, search, page]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.set("page", "1");
    setParams(next, { replace: false });
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            1:1 문의 관리
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            전체 {data.total.toLocaleString()}건
          </div>
        </div>
        <CsvExportButton kind="services" />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <FilterPills
          value={status}
          onChange={(v) => setParam("status", v)}
          options={[
            { label: "전체", value: "" },
            { label: "답변 대기", value: "0" },
            { label: "답변 완료", value: "1" },
          ]}
        />
        <div style={{ flex: 1 }} />
        <SearchBar
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={() => setParam("q", searchInput.trim())}
          placeholder="제목 / 작성자 / 본문"
        />
      </div>

      <Panel title="문의 목록" count={data.total}>
        {loading ? (
          <SkeletonRows count={8} />
        ) : error ? (
          <Empty label={error} />
        ) : data.items.length === 0 ? (
          <Empty />
        ) : (
          data.items.map((s) => (
            <Row
              key={s.docId}
              onClick={() => navigate(`/miadmin/services/${s.docId}`)}
              left={
                <span
                  style={{
                    display: "inline-flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {s.title || "—"}
                  </span>
                  <ServiceStatusChip status={s.status} />
                </span>
              }
              mid={`${s.name || "—"} · ${s.type || "—"}`}
              right={relativeTime(s.createDate)}
            />
          ))
        )}
        <Pagination
          page={page}
          limit={20}
          total={data.total}
          onChange={(p) => setParam("page", String(p))}
        />
      </Panel>
    </>
  );
}

export function AdminServiceDetail() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState("");

  const reload = async () => {
    setLoading(true);
    setError("");
    const r = await getAdminServiceApi(docId);
    if (r?.success) {
      setData(r.data);
      setAnswer(r.data?.comment || "");
    } else {
      setError(r?.error || "조회 실패");
    }
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await reload();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const submit = async () => {
    if (!answer.trim()) return;
    setSubmitting(true);
    setDone("");
    const r = await answerAdminServiceApi(docId, answer.trim());
    setSubmitting(false);
    if (r?.success) {
      setDone("답변이 등록되었습니다.");
      await reload();
    } else {
      setError(r?.error || "답변 등록 실패");
    }
  };

  const s = data || {};
  const isDone = String(s.status) === "1";

  return (
    <DetailFrame
      title="1:1 문의 상세"
      backTo="/miadmin/services"
      onBack={() => navigate(-1)}
      badge={data ? <ServiceStatusChip status={s.status} /> : null}
    >
      {loading ? (
        <SkeletonRows count={5} />
      ) : error ? (
        <Empty label={error} />
      ) : (
        <>
          <InfoRow label="제목" value={s.title} />
          <InfoRow label="분야" value={s.type} />
          <InfoRow
            label="작성자"
            value={`${s.name || ""} (${s.userType || ""})`}
          />
          <InfoRow label="작성자 ID" value={s.id} mono />
          <InfoRow label="등록일" value={formatDate(s.createDate)} />

          <div style={{ height: 12 }} />
          <div
            style={{
              fontSize: 13,
              color: C.sub,
              fontWeight: 700,
              padding: "8px 0",
            }}
          >
            문의 내용
          </div>
          <div
            style={{
              padding: "14px 16px",
              background: C.bg,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              fontSize: 14,
              color: C.body,
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              minHeight: 60,
            }}
          >
            {s.body || "—"}
          </div>

          <div style={{ height: 18 }} />
          <div
            style={{
              fontSize: 13,
              color: C.sub,
              fontWeight: 700,
              padding: "8px 0",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            관리자 답변 {isDone && <ServiceStatusChip status="1" />}
          </div>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="문의자에게 전달할 답변을 작성하세요."
            disabled={submitting}
            rows={6}
            style={{
              width: "100%",
              minHeight: 140,
              padding: "12px 14px",
              borderRadius: 10,
              border: `1px solid ${C.line}`,
              fontSize: 14,
              color: C.ink,
              background: "#fff",
              outline: "none",
              fontFamily: "inherit",
              boxSizing: "border-box",
              resize: "vertical",
              lineHeight: 1.6,
            }}
          />

          {done && (
            <div
              style={{
                marginTop: 10,
                padding: "10px 12px",
                borderRadius: 10,
                background: C.greenBg,
                border: `1px solid ${C.greenBorder}`,
                color: C.greenDark,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {done}
            </div>
          )}

          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
            }}
          >
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !answer.trim()}
              style={{
                ...primaryBtn,
                background:
                  submitting || !answer.trim() ? "#9CA3AF" : C.greenDark,
                cursor:
                  submitting || !answer.trim() ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "등록 중..." : isDone ? "답변 수정 등록" : "답변 등록"}
            </button>
          </div>
        </>
      )}
    </DetailFrame>
  );
}
